import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Check, Target, Info, Search as SearchIcon, Megaphone, Trophy, Monitor, Radio, Tv, ArrowUp, ArrowDown, Minus,
  Globe, MapPin, Eye, MousePointerClick, TrendingUp, Layers, Footprints,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { sr } from "../../../lib/sr";
import { refreshCurrency } from "../../../lib/useCurrency";
import { auth } from "../../../lib/api";
import MarginReport from "../MarginReport";
import { Head, Honest } from "./shared";

export function Results() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("performance");
  const user = auth.getUser();
  const isAdmin = user && ["admin", "super_admin"].includes(user.role);
  useEffect(() => { sr.results().then(setData).catch(() => setData({ has_data: false })); }, []);
  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const EV = { PROJECTED: "sr-badge-gray", OBSERVED: "sr-badge", MODELED: "sr-badge-amber", VERIFIED: "sr-badge-green" };
  return (
    <div>
      <Head title="Results" sub="What produced visits, leads, calls and sales — labeled by evidence level." />
      {isAdmin && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }} data-testid="results-tabs">
          <button className={`sr-btn sr-btn-sm ${tab === "performance" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setTab("performance")} data-testid="results-tab-performance">Performance</button>
          <button className={`sr-btn sr-btn-sm ${tab === "margin" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setTab("margin")} data-testid="results-tab-margin">Margin</button>
        </div>
      )}
      {isAdmin && tab === "margin" ? <MarginReport embedded /> : (<>
      <UnifiedDashboard />
      {data.has_data ? (
        <>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
            {(data.outcomes || []).map((o) => (
              <div key={o.metric} className="sr-card">
                <span className={`sr-badge ${EV[o.evidence]}`}>{o.evidence}</span>
                <div className="sr-stat-val" style={{ marginTop: 10 }}>{o.value.toLocaleString()}</div>
                <div className="sr-stat-label">{o.metric}</div>
                {o.cost_per != null && <div className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>${o.cost_per} each · {o.trend}</div>}
              </div>
            ))}
          </div>
          {data.demo && <p className="sr-muted" style={{ marginTop: 12, fontSize: 12 }}><Info size={11} /> Demo data shown for illustration.</p>}
        </>
      ) : (
        <Honest icon={Target} title="No live results yet" body={data.message || "Results appear once an approved plan is live and delivering."} />
      )}

      <VerifiedResponses />
      <ChannelComparison />
      <FootfallExposure />
      <VisitStudies />

      {data.methodology && (
        <div className="sr-card" style={{ marginTop: 18 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Methodology</div>
          {Object.entries(data.methodology.evidence_levels || {}).map(([k, v]) => (
            <p key={k} style={{ fontSize: 13, marginBottom: 6 }}><strong style={{ color: "var(--navy)" }}>{k}:</strong> <span className="sr-muted">{v}</span></p>
          ))}
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Attribution: {data.methodology.attribution_window}. {data.methodology.limitations}</p>
        </div>
      )}
      </>)}
    </div>
  );
}

function VerifiedResponses() {
  const [data, setData] = useState(null);
  useEffect(() => { sr.eventsSummary().then(setData).catch(() => setData({ verified_total: 0, by_type: {} })); }, []);
  if (!data) return null;
  return (
    <div className="sr-card" style={{ marginTop: 18 }} data-testid="verified-responses">
      <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Verified responses <span className="sr-badge sr-badge-green" style={{ marginLeft: 6 }}>VERIFIED</span></div>
      {data.verified_total === 0 ? (
        <p className="sr-muted" style={{ fontSize: 14 }}>No verified responses yet. Share a campaign's promo/QR code — every redemption is recorded here as verified evidence.</p>
      ) : (
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div className="sr-stat"><div className="sr-stat-val">{data.verified_total}</div><div className="sr-stat-label">Total verified actions</div></div>
          {Object.entries(data.by_type).map(([k, v]) => (
            <div key={k} className="sr-stat"><div className="sr-stat-val">{v}</div><div className="sr-stat-label" style={{ textTransform: "capitalize" }}>{k}s</div></div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitStudies() {
  const [studies, setStudies] = useState(null);
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => sr.listStudies().then((r) => setStudies(r.studies || [])).catch(() => setStudies([]));
  useEffect(() => { load(); }, []);
  const check = async () => { setBusy(true); try { setElig(await sr.studyEligibility()); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const start = async () => { setBusy(true); try { await sr.createStudy({}); toast.success("Visit study started"); setElig(null); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const s = (studies || []).find((x) => x.results) || (studies || [])[0];
  const r = s?.results;
  const maxExp = r ? Math.max(...r.exposed_vs_control.map((w) => Math.max(w.exposed, w.control))) : 1;
  return (
    <div className="sr-card" style={{ marginTop: 18 }} data-testid="visit-intelligence">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="sr-eyebrow">Visit Intelligence</div>
        {!s && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={check} disabled={busy} data-testid="study-eligibility-btn">Check eligibility</button>}
      </div>
      {elig && !s && (
        <div style={{ marginTop: 12 }}>
          {elig.checks.map((c) => (
            <div key={c.label} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, padding: "4px 0" }}>
              {c.ok ? <Check size={15} color="var(--success)" /> : <Info size={15} color="var(--muted)" />}
              <span className={c.ok ? "" : "sr-muted"}>{c.label}</span>
            </div>
          ))}
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>{elig.note}</p>
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} onClick={start} disabled={!elig.eligible || busy} data-testid="study-start-btn">Start visit study</button>
        </div>
      )}
      {!s && !elig && <p className="sr-muted" style={{ fontSize: 14, marginTop: 8 }}>Measure incremental store visits with a privacy-safe lift study (observed vs control).</p>}
      {s && s.status === "collecting" && <p className="sr-muted" style={{ fontSize: 14, marginTop: 8 }}>Study collecting — {s.note}</p>}
      {r && (
        <div style={{ marginTop: 12 }}>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
            <div className="sr-stat"><span className="sr-badge sr-badge">OBSERVED</span><div className="sr-stat-val" style={{ marginTop: 8 }}>{r.observed_visits}</div><div className="sr-stat-label">Observed visits</div></div>
            <div className="sr-stat"><span className="sr-badge sr-badge-amber">MODELED</span><div className="sr-stat-val" style={{ marginTop: 8 }}>{r.modeled_incremental_visits}</div><div className="sr-stat-label">Incremental (+{r.lift_pct}% lift)</div></div>
            <div className="sr-stat"><span className="sr-badge sr-badge-green">VERIFIED</span><div className="sr-stat-val" style={{ marginTop: 8 }}>{r.verified.redemptions}</div><div className="sr-stat-label">Redemptions</div></div>
            <div className="sr-stat"><div className="sr-stat-label">Cost / incremental visit</div><div className="sr-stat-val">${r.cost_per_incremental_visit}</div></div>
          </div>
          <div style={{ marginTop: 14 }}>
            <strong style={{ color: "var(--navy)", fontSize: 14 }}>Exposed vs control</strong>
            {r.exposed_vs_control.map((w) => (
              <div key={w.week} style={{ margin: "8px 0" }}>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>{w.week}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{ height: 12, width: `${(w.exposed / maxExp) * 70}%`, background: "var(--blue)", borderRadius: 4 }} />
                  <span style={{ fontSize: 12 }}>{w.exposed} exposed</span>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{ height: 12, width: `${(w.control / maxExp) * 70}%`, background: "var(--border)", borderRadius: 4 }} />
                  <span style={{ fontSize: 12 }}>{w.control} control</span>
                </div>
              </div>
            ))}
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 10 }}>Match rate {r.match_rate_pct}% · {r.attribution_window} · confidence {r.confidence}. {r.limitations}</p>
        </div>
      )}
    </div>
  );
}


function FootfallExposure() {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState("hour");
  useEffect(() => { sr.footfallExposure().then(setD).catch(() => setD({ enabled: false })); }, []);
  if (!d || !d.enabled) return null;
  const TONE = { gray: "sr-badge-gray", blue: "sr-badge-blue", green: "sr-badge-green", amber: "sr-badge-amber", red: "sr-badge-red" };
  const r = d.report;
  const series = r ? (mode === "hour" ? r.by_hour : r.by_day) : [];
  const peak = series.length ? Math.max(...series.map((x) => x.v)) : 1;
  const num = (n) => Number(n || 0).toLocaleString();
  const stats = r ? [
    { l: "Attributed visits", v: num(r.attributed_visits) },
    { l: "Projected visits", v: num(r.projected_visits) },
    { l: "Visitation lift", v: `+${r.visitation_lift_pct}%` },
    { l: "Avg eCPV", v: `$${r.ecpv_usd}` },
  ] : [];
  return (
    <div className="sr-card" style={{ marginTop: 18 }} data-testid="footfall-exposure">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0 }}><Footprints size={13} /> Visits after ad exposure</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className={`sr-badge ${TONE[d.tone] || "sr-badge-gray"}`} data-testid="footfall-status">{d.label}</span>
          {d.is_sample && <span className="sr-badge sr-badge-amber" data-testid="footfall-sample-badge"><Info size={11} /> SAMPLE</span>}
        </div>
      </div>

      {!r ? (
        <p className="sr-muted" style={{ fontSize: 14, marginTop: 10 }} data-testid="footfall-status-msg">{d.description} {d.what_next}</p>
      ) : (
        <div style={{ marginTop: 12 }}>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
            {stats.map((s) => (
              <div key={s.l} className="sr-stat" data-testid={`footfall-stat-${s.l.split(" ")[0].toLowerCase()}`}>
                <div className="sr-stat-val">{s.v}</div><div className="sr-stat-label">{s.l}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "16px 0 8px" }}>
            <strong style={{ color: "var(--navy)", fontSize: 14 }}>When visits happened</strong>
            <div style={{ display: "inline-flex", gap: 4 }} data-testid="footfall-mode-toggle">
              <button className={`sr-btn sr-btn-sm ${mode === "hour" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setMode("hour")} data-testid="footfall-mode-hour">Hour</button>
              <button className={`sr-btn sr-btn-sm ${mode === "day" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setMode("day")} data-testid="footfall-mode-day">Day</button>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                <XAxis dataKey="k" tick={{ fontSize: 11 }} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={num} />
                <Tooltip cursor={{ fill: "rgba(79,134,247,.08)" }} formatter={(v) => [`${num(v)} visits`, "Visits"]} />
                <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                  {series.map((x, i) => <Cell key={i} fill={x.v === peak ? "var(--blue,#2563eb)" : "#9DB7E8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 14 }} data-testid="footfall-by-location">
            <strong style={{ color: "var(--navy)", fontSize: 14 }}>Visits by location</strong>
            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table className="sr-table" style={{ width: "100%", fontSize: 13 }}>
                <thead><tr><th style={{ textAlign: "left" }}>Location</th><th>Visits</th><th>Impression</th><th>Click</th></tr></thead>
                <tbody>
                  {r.by_location.map((l) => (
                    <tr key={l.name}><td><strong>{l.name}</strong></td><td style={{ textAlign: "center" }}>{num(l.visits)}</td><td style={{ textAlign: "center" }}>{num(l.impression_visits)}</td><td style={{ textAlign: "center" }}>{num(l.click_visits)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 10, display: "flex", gap: 6 }}>
            <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            {d.is_sample && <strong style={{ color: "#92400E" }}>Sample data for illustration. </strong>}
            {r.privacy_note} {r.attribution_window} · match rate {r.match_rate_pct}% · confidence {r.confidence}.
          </p>
        </div>
      )}
    </div>
  );
}


function TrendArrow({ t }) {
  if (!t) return null;
  const up = t.direction === "up";
  const flat = t.direction === "flat";
  const color = flat ? "var(--muted)" : (up ? "#067647" : "#B42318");
  const Ico = flat ? Minus : (up ? ArrowUp : ArrowDown);
  const label = t.change_pct != null ? `${Math.abs(t.change_pct)}%` : (up ? "up" : "down");
  const title = t.metric === "cpa" ? "Cost-per-result week-over-week" : "Conversions week-over-week";
  return (
    <span title={title} data-testid="channel-trend" style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 12, fontWeight: 700, color }}>
      <Ico size={13} />{!flat && label}
    </span>
  );
}

function ChannelComparison() {
  const [data, setData] = useState(null);
  useEffect(() => { sr.channelComparison().then(setData).catch(() => setData({ channels: [], has_data: false })); }, []);
  if (!data) return null;
  const ICON = { google: SearchIcon, meta: Megaphone, display: Monitor, radio: Radio, dooh: Tv };
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const shown = (data.channels || []).filter((c) => c.has_data);
  return (
    <div className="sr-card" style={{ marginTop: 18 }} data-testid="channel-comparison">
      <div className="sr-eyebrow" style={{ marginBottom: 4 }}>Channel scoreboard</div>
      <p className="sr-muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>Compare every paid channel — Google, Meta, Display, Radio and DOOH — at a glance: spend, clicks and cost-per-result from recorded performance.</p>
      {!data.has_data ? (
        <p className="sr-muted" style={{ fontSize: 14 }} data-testid="channel-comparison-empty">No channel performance recorded yet. Once your campaigns are live and reporting, they'll compare here.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
          {shown.map((c) => {
            const Ico = ICON[c.key] || Target;
            const best = data.best_cpa === c.key;
            return (
              <div key={c.key} className="sr-card" data-testid={`channel-${c.key}`}
                style={{ border: best ? "2px solid var(--success, #067647)" : "1px solid var(--border)", background: best ? "#F2FBF5" : "var(--card, #fff)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "var(--navy)" }}><Ico size={16} /> {c.label} <TrendArrow t={c.trend} /></div>
                  {best && <span className="sr-badge sr-badge-green" data-testid={`channel-best-${c.key}`}><Trophy size={11} /> Best cost-per-result</span>}
                </div>
                {c.has_data ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
                    <div><div className="sr-muted" style={{ fontSize: 11 }}>Spend</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{money(c.spend)}</div></div>
                    <div><div className="sr-muted" style={{ fontSize: 11 }}>Clicks</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{c.clicks.toLocaleString()}</div></div>
                    <div><div className="sr-muted" style={{ fontSize: 11 }}>Conversions</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{c.conversions.toLocaleString()}</div></div>
                    <div><div className="sr-muted" style={{ fontSize: 11 }}>Cost / result</div><div style={{ fontWeight: 700, color: best ? "#067647" : "var(--navy)" }} data-testid={`channel-cpa-${c.key}`}>{c.cpa != null ? money(c.cpa) : "—"}</div></div>
                    <div><div className="sr-muted" style={{ fontSize: 11 }}>CPC</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{c.cpc != null ? money(c.cpc) : "—"}</div></div>
                    <div><div className="sr-muted" style={{ fontSize: 11 }}>CTR</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{c.ctr != null ? `${c.ctr}%` : "—"}</div></div>
                    {c.committed && <div style={{ gridColumn: "1 / -1", fontSize: 11 }} className="sr-muted" data-testid={`channel-committed-${c.key}`}>Committed managed budget — radio isn't click-tracked, so it has no week-over-week trend (that needs tracked conversions).</div>}
                  </div>
                ) : (
                  <p className="sr-muted" style={{ fontSize: 13, marginTop: 12 }} data-testid={`channel-nodata-${c.key}`}>No data yet for this channel.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ---- Unified performance dashboard: Programmatic + Meta + Google PPC in one view ----
const CH_ICON = { programmatic: Layers, meta: Megaphone, google: SearchIcon };

function UnifiedDashboard() {
  const [data, setData] = useState(null);
  const [region, setRegionState] = useState(null);
  const [days, setDays] = useState(30);
  const [switching, setSwitching] = useState(false);

  const load = (d = days) => sr.unifiedDashboard(d).then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); sr.getRegion().then(setRegionState).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const changeRegion = async (code) => {
    setSwitching(true);
    try {
      const r = await sr.setRegion(code);
      setRegionState((prev) => ({ ...(prev || {}), ...r }));
      refreshCurrency();
      await load();
      toast.success(code ? `Switched to ${r.currency}` : "Region set to auto-detect");
    } catch { toast.error("Couldn't change region"); }
    finally { setSwitching(false); }
  };
  const changeDays = (d) => { setDays(d); load(d); };

  if (!data) return <div className="sr-card" style={{ marginBottom: 18 }} data-testid="unified-dashboard-loading"><Loader2 className="sr-spin" size={18} /></div>;
  if (data.error) return null;

  const cur = data.currency || { symbol: "$", usd_to_local: 1, currency: "USD" };
  const fx = cur.usd_to_local || 1;
  const money = (n) => `${cur.symbol}${Number((n || 0) * fx).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const num = (n) => Number(n || 0).toLocaleString();
  const t = data.totals || {};
  const maxGeo = Math.max(1, ...(data.geography || []).map((g) => g.impressions));

  const kpis = [
    { label: "Impressions", value: num(t.impressions), icon: Eye },
    { label: "Ad spend", value: money(t.spend), icon: TrendingUp },
    { label: "Avg CPM", value: t.cpm != null ? money(t.cpm) : "—", icon: Layers },
    { label: "Clicks", value: num(t.clicks), icon: MousePointerClick },
    { label: "CTR", value: t.ctr != null ? `${t.ctr}%` : "—", icon: Target },
    { label: "Reach (est.)", value: num(t.reach), icon: Globe },
  ];

  return (
    <div className="sr-card" style={{ marginBottom: 18 }} data-testid="unified-dashboard">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Layers size={13} /> Unified performance · Programmatic · Meta · Google
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* Region / currency switcher */}
          <div style={{ display: "inline-flex", gap: 4 }} data-testid="dashboard-region-switch">
            <button className={`sr-btn sr-btn-sm ${!region?.override ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => changeRegion(null)} disabled={switching} data-testid="region-auto">Auto</button>
            {(region?.available || []).map((r) => (
              <button key={r.code} className={`sr-btn sr-btn-sm ${region?.override === r.code ? "sr-btn-primary" : "sr-btn-ghost"}`}
                onClick={() => changeRegion(r.code)} disabled={switching} data-testid={`region-${r.code}`} title={r.label}>{r.flag} {r.currency}</button>
            ))}
          </div>
          <div style={{ display: "inline-flex", gap: 4 }}>
            {[7, 30, 90].map((d) => (
              <button key={d} className={`sr-btn sr-btn-sm ${days === d ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => changeDays(d)} data-testid={`dashboard-days-${d}`}>{d}d</button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {data.is_demo && <span className="sr-badge sr-badge-amber" data-testid="dashboard-demo-badge"><Info size={11} /> Sample data</span>}
        <span className="sr-muted" style={{ fontSize: 12 }}>
          {cur.is_live_fx ? `Figures in ${cur.currency} · live rate 1 USD = ${cur.usd_to_local} ${cur.currency}` : `Figures in ${cur.currency}`}
        </span>
      </div>

      {/* KPI strip */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
        {kpis.map((k) => (
          <div key={k.label} className="sr-card" style={{ padding: 14 }} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="sr-muted" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}><k.icon size={12} /> {k.label}</div>
            <div className="sr-stat-val" style={{ marginTop: 6, fontSize: 22 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Delivery trend */}
      {(data.trend || []).length > 0 && (
        <div style={{ marginTop: 16 }} data-testid="dashboard-trend">
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Daily impressions delivered</div>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--blue,#2563eb)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="var(--blue,#2563eb)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} minTickGap={24} />
                <YAxis tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                <Tooltip formatter={(v, n) => [num(v), n === "impressions" ? "Impressions" : n]} labelFormatter={(l) => l} />
                <Area type="monotone" dataKey="impressions" stroke="var(--blue,#2563eb)" strokeWidth={2} fill="url(#impGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Channel breakdown */}
      <div style={{ marginTop: 14 }}>
        <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }}>By channel</div>
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 12 }}>
          {(data.channels || []).map((c) => {
            const Ico = CH_ICON[c.key] || Target;
            return (
              <div key={c.key} className="sr-card" data-testid={`dashboard-channel-${c.key}`} style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <strong style={{ color: "var(--navy)", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}><Ico size={14} /> {c.label}</strong>
                  <span className={`sr-badge ${data.sources?.[c.key] === "live" ? "sr-badge-green" : "sr-badge-gray"}`} data-testid={`dashboard-source-${c.key}`}>{data.sources?.[c.key] === "live" ? "LIVE" : "MODELED"}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
                  <div><div className="sr-muted" style={{ fontSize: 11 }}>Impressions</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{num(c.impressions)}</div></div>
                  <div><div className="sr-muted" style={{ fontSize: 11 }}>CPM</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{c.cpm != null ? money(c.cpm) : "—"}</div></div>
                  <div><div className="sr-muted" style={{ fontSize: 11 }}>Spend</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{money(c.spend)}</div></div>
                  <div><div className="sr-muted" style={{ fontSize: 11 }}>Clicks</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{num(c.clicks)}</div></div>
                  <div><div className="sr-muted" style={{ fontSize: 11 }}>CTR</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{c.ctr != null ? `${c.ctr}%` : "—"}</div></div>
                  <div><div className="sr-muted" style={{ fontSize: 11 }}>Conversions</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{num(c.conversions)}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Impressions by geography */}
      {(data.geography || []).length > 0 && (
        <div style={{ marginTop: 16 }} data-testid="dashboard-geography">
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 5 }}><MapPin size={12} /> Where impressions were delivered</div>
          {(data.geography || []).map((g) => (
            <div key={g.region} style={{ marginBottom: 8 }} data-testid={`geo-row-${g.region.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <strong style={{ color: "var(--navy)" }}>{g.region}</strong>
                <span className="sr-muted">{num(g.impressions)} · {g.pct}%</span>
              </div>
              <div className="sr-progress" style={{ margin: "3px 0" }}><div style={{ width: `${Math.round((g.impressions / maxGeo) * 100)}%` }} /></div>
            </div>
          ))}
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>{data.geography_note}</p>
        </div>
      )}

      <p className="sr-muted" style={{ fontSize: 12, marginTop: 12 }}><Info size={11} /> {data.note}</p>
    </div>
  );
}
